import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DeviceTablet, Trash, ArrowsClockwise, Copy, ShieldCheck } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—");

export default function KioskDevicesDialog({ open, onOpenChange }) {
  const [data, setData] = useState({ pairing: null, devices: [] });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hr/kiosk/devices").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);

  const genCode = async () => {
    setBusy(true);
    try { await api.post("/hr/kiosk/pairing-code"); await load(); toast.success("Código generado · válido 60 min, un solo uso"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const cancelCode = async () => { await api.delete("/hr/kiosk/pairing-code"); await load(); };
  const revoke = async (d) => {
    if (!window.confirm(`¿Revocar "${d.name}"? Dejará de poder checar hasta emparejarlo de nuevo.`)) return;
    await api.delete(`/hr/kiosk/devices/${d.id}`); await load(); toast.success("Dispositivo revocado");
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copiado"); };

  const p = data.pairing;
  const active = p && !p.expired;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="kiosk-devices-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><DeviceTablet size={20} weight="duotone" className="text-[#002FA7]" /> Kiosco · dispositivos autorizados</DialogTitle>
          <DialogDescription>Solo las tablets emparejadas pueden usar el reloj checador y ver datos de empleados.</DialogDescription>
        </DialogHeader>

        <div className="border border-zinc-200 p-4 bg-zinc-50" data-testid="kiosk-pairing-box">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Código de emparejamiento</div>
          {active ? (
            <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
              <div>
                <code className="font-mono-data text-3xl font-bold tracking-widest text-[#002FA7]" data-testid="kiosk-pairing-code">{p.code}</code>
                <div className="text-[11px] text-zinc-500 mt-1">Vence {fmt(p.expires_at)} · un solo uso · escríbelo en la tablet en <b>/reloj-checador</b></div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => copy(p.code)} data-testid="kiosk-code-copy"><Copy size={14} /></Button>
                <Button size="sm" variant="outline" onClick={cancelCode} data-testid="kiosk-code-cancel">Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
              <div className="text-sm text-zinc-600">{p?.expired ? "El último código venció." : "No hay código activo."} Genera uno para emparejar una tablet nueva.</div>
              <Button size="sm" onClick={genCode} disabled={busy} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-1" data-testid="kiosk-code-generate"><ArrowsClockwise size={14} /> Generar código</Button>
            </div>
          )}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">Dispositivos emparejados ({data.devices.length})</div>
          {data.devices.length === 0 ? (
            <div className="text-sm text-zinc-400 italic border border-dashed border-zinc-200 p-4 text-center" data-testid="kiosk-devices-empty">Ningún dispositivo emparejado. El reloj checador está bloqueado.</div>
          ) : (
            <ul className="divide-y divide-zinc-100 border border-zinc-200" data-testid="kiosk-devices-list">
              {data.devices.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm" data-testid={`kiosk-device-${d.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <ShieldCheck size={16} weight="duotone" className="text-emerald-600 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{d.name}</div>
                      <div className="text-[11px] text-zinc-500">Emparejado {fmt(d.paired_at)} · última actividad {fmt(d.last_seen_at)}{d.ip ? ` · IP ${d.ip}` : ""}</div>
                    </div>
                  </div>
                  <button onClick={() => revoke(d)} className="p-2 hover:bg-red-50 text-[#E0000F] rounded" title="Revocar" data-testid={`kiosk-device-revoke-${d.id}`}><Trash size={15} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
